import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Target, Lightbulb, CheckCircle2, XCircle, AlertCircle,
  TrendingUp, TrendingDown, FileText, Loader2, Plus, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AppResultsPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!report);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!report && id) {
      fetchReport();
    }
  }, [id, report]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Report not found');
      navigate('/app/history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/reports/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition or generate
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'analysis_report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        toast.error('Export limit reached. Upgrade for more exports.');
        navigate('/pricing');
      } else {
        toast.error('Failed to download PDF');
      }
    } finally {
      setDownloading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "score-excellent";
    if (score >= 60) return "score-moderate";
    return "score-weak";
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return "Excellent Match";
    if (score >= 75) return "Strong Match";
    if (score >= 60) return "Moderate Match";
    if (score >= 40) return "Weak Match";
    return "Poor Match";
  };

  const getScoreBgColor = (score) => {
    if (score >= 75) return "bg-lime-500";
    if (score >= 60) return "bg-orange-500";
    return "bg-red-500";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "badge-error";
      case "medium": return "badge-warning";
      default: return "badge-info";
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case "critical": return "badge-error";
      case "important": return "badge-warning";
      default: return "badge-info";
    }
  };

  const getMatchStatusColor = (status) => {
    switch (status) {
      case "matched": return "badge-success";
      case "partial": return "badge-warning";
      default: return "badge-error";
    }
  };

  const matchedSkills = report.skills_analysis?.filter(s => s.found_in_resume).length || 0;
  const totalSkills = report.skills_analysis?.length || 0;
  const matchedKeywords = report.keywords_analysis?.filter(k => k.match_status === "matched").length || 0;
  const totalKeywords = report.keywords_analysis?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="results-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              variant="ghost"
              onClick={() => navigate('/app/history')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to History
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleDownloadPdf}
              disabled={downloading}
              variant="outline"
              className="border-slate-200"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button
              onClick={() => navigate('/app/analyze')}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
          </div>
        </div>

        {/* Job Info */}
        {(report.job_title || report.company) && (
          <div className="mb-6">
            <h1 className="font-heading font-bold text-2xl text-slate-900">
              {report.job_title || 'Analysis Results'}
            </h1>
            {report.company && (
              <p className="text-slate-600">{report.company}</p>
            )}
          </div>
        )}

        {/* Score Hero Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="md:col-span-2 bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardContent className="p-8 flex items-center gap-8">
              <div className="relative">
                <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 144 144">
                  <circle cx="72" cy="72" r="70" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                  <circle
                    cx="72" cy="72" r="70" fill="none"
                    className={`score-ring ${getScoreColor(report.overall_score)}`}
                    strokeWidth="8" strokeLinecap="round" strokeDasharray="440"
                    strokeDashoffset={440 - (440 * report.overall_score) / 100}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`font-heading font-extrabold text-4xl ${getScoreColor(report.overall_score)}`}
                    data-testid="overall-score">
                    {report.overall_score}
                  </span>
                  <span className="text-sm text-slate-500">/ 100</span>
                </div>
              </div>
              
              <div className="flex-1">
                <Badge className={`mb-2 ${getScoreBgColor(report.overall_score)} text-white`}>
                  {getScoreLabel(report.overall_score)}
                </Badge>
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">Match Score</h2>
                <p className="text-slate-600 text-sm leading-relaxed">{report.summary}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-lime-600" />
                </div>
                <span className="font-heading font-semibold text-slate-900">Skills Match</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Matched</span>
                  <span className="font-semibold text-slate-900">{matchedSkills} / {totalSkills}</span>
                </div>
                <Progress value={totalSkills > 0 ? (matchedSkills / totalSkills) * 100 : 0} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <span className="font-heading font-semibold text-slate-900">Keywords</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Found</span>
                  <span className="font-semibold text-slate-900">{matchedKeywords} / {totalKeywords}</span>
                </div>
                <Progress value={totalKeywords > 0 ? (matchedKeywords / totalKeywords) * 100 : 0} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 font-heading text-lg">
                <div className="w-8 h-8 bg-lime-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-lime-600" />
                </div>
                Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.strengths?.map((strength, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-lime-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{strength}</span>
                  </li>
                ))}
                {(!report.strengths || report.strengths.length === 0) && (
                  <li className="text-slate-500">No specific strengths identified</li>
                )}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 font-heading text-lg">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-orange-600" />
                </div>
                Areas for Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.weaknesses?.map((weakness, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{weakness}</span>
                  </li>
                ))}
                {(!report.weaknesses || report.weaknesses.length === 0) && (
                  <li className="text-slate-500">No specific weaknesses identified</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analysis Tabs */}
        <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
          <Tabs defaultValue="suggestions" className="w-full">
            <CardHeader className="pb-0 border-b border-slate-100">
              <TabsList className="bg-transparent h-auto p-0 gap-6">
                <TabsTrigger value="suggestions" className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none pb-4 font-heading font-semibold">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Suggestions ({report.suggestions?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="skills" className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none pb-4 font-heading font-semibold">
                  <Target className="w-4 h-4 mr-2" />
                  Skills ({report.skills_analysis?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="keywords" className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none pb-4 font-heading font-semibold">
                  <FileText className="w-4 h-4 mr-2" />
                  Keywords ({report.keywords_analysis?.length || 0})
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="p-6">
              <TabsContent value="suggestions" className="m-0">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4">
                    {report.suggestions?.map((suggestion, index) => (
                      <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-heading font-semibold text-slate-900">{suggestion.section}</span>
                          <Badge className={getPriorityColor(suggestion.priority)}>{suggestion.priority} priority</Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                            <p className="text-sm text-slate-600">{suggestion.issue}</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-lime-500 mt-1 flex-shrink-0" />
                            <p className="text-sm text-slate-700 font-medium">{suggestion.suggestion}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!report.suggestions || report.suggestions.length === 0) && (
                      <p className="text-center text-slate-500 py-8">No suggestions available</p>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="skills" className="m-0">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {report.skills_analysis?.map((skill, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${skill.found_in_resume ? 'bg-lime-50 border-lime-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {skill.found_in_resume ? (
                              <CheckCircle2 className="w-5 h-5 text-lime-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <span className="font-medium text-slate-900">{skill.skill}</span>
                          </div>
                          <Badge className={getImportanceColor(skill.importance)}>{skill.importance}</Badge>
                        </div>
                      </div>
                    ))}
                    {(!report.skills_analysis || report.skills_analysis.length === 0) && (
                      <p className="text-center text-slate-500 py-8 col-span-2">No skills analysis available</p>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="keywords" className="m-0">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {report.keywords_analysis?.map((keyword, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3">
                          <code className="font-mono text-sm bg-slate-200 px-2 py-1 rounded">{keyword.keyword}</code>
                          <Badge className={getMatchStatusColor(keyword.match_status)}>{keyword.match_status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-500">Resume: <span className="font-semibold text-slate-900">{keyword.count_in_resume}</span></span>
                          <span className="text-slate-500">Job: <span className="font-semibold text-slate-900">{keyword.count_in_job}</span></span>
                        </div>
                      </div>
                    ))}
                    {(!report.keywords_analysis || report.keywords_analysis.length === 0) && (
                      <p className="text-center text-slate-500 py-8">No keywords analysis available</p>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
}
